import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./highchartsPtBr";
import { PainelVisivelContext } from "../contexto/painelVisivel";

interface Props {
  opcoes: Highcharts.Options;
  centro?: (diametroFuro: number) => React.ReactNode;
}

interface Furo {
  x: number;
  y: number;
  diametro: number;
}

const furoDaRosca = (chart: Highcharts.Chart): Furo | null => {
  const serie = chart.series[0] as any;
  if (!serie || !serie.center) {
    return null;
  }
  const [x, y, , diametro] = serie.center as number[];
  return { x: chart.plotLeft + x, y: chart.plotTop + y, diametro };
};

const mesmoFuro = (a: Furo | null, b: Furo | null) =>
  a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.diametro === b.diametro);

const sobreposicaoCentral = (furo: Furo | null): React.CSSProperties => ({
  position: "absolute",
  ...(furo
    ? { left: furo.x - furo.diametro / 2, top: furo.y - furo.diametro / 2, width: furo.diametro, height: furo.diametro }
    : { top: 0, right: 0, bottom: 0, left: 0 }),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  pointerEvents: "none",
});

const Grafico = ({ opcoes, centro }: Props) => {
  const [furo, setFuro] = useState<Furo | null>(null);

  // Com animacao, o setSize anima a largura da moldura e, se um reflow chega no meio, a animacao antiga termina
  // por cima com a largura velha e o grafico fica cortado
  const opcoesSemExportacao = useMemo(
    () => ({
      ...opcoes,
      chart: { ...opcoes.chart, animation: false },
      exporting: { ...opcoes.exporting, enabled: false },
    }),
    [opcoes]
  );

  const visivel = useContext(PainelVisivelContext);
  const grafico = useRef<Highcharts.Chart | null>(null);
  const moldura = useRef<HTMLDivElement>(null);
  const comCentro = !!centro;

  const aoCriar = useCallback(
    (chart: Highcharts.Chart) => {
      grafico.current = chart;
      if (!comCentro) {
        return;
      }
      const atualizar = () => {
        const novo = furoDaRosca(chart);
        setFuro((atual) => (mesmoFuro(atual, novo) ? atual : novo));
      };
      atualizar();
      Highcharts.addEvent(chart, "render", atualizar);
    },
    [comCentro]
  );

  // Highcharts 8 so reage a resize da janela; em aba oculta (largura 0) ele assume 600px e nao se corrige sozinho
  const reajustar = useCallback(() => {
    const chart = grafico.current;
    if (chart && chart.renderer) {
      chart.reflow();
    }
  }, []);

  useEffect(() => {
    if (visivel) {
      reajustar();
    }
  }, [visivel, reajustar]);

  useEffect(() => {
    const Observador = (window as any).ResizeObserver;
    if (!moldura.current || typeof Observador !== "function") {
      return undefined;
    }
    const observador = new Observador(reajustar);
    observador.observe(moldura.current);
    return () => observador.disconnect();
  }, [reajustar]);

  return (
    <div ref={moldura} style={{ position: "relative" }}>
      <HighchartsReact highcharts={Highcharts} options={opcoesSemExportacao} callback={aoCriar} />
      {centro && <div style={sobreposicaoCentral(furo)}>{centro(furo ? furo.diametro : 0)}</div>}
    </div>
  );
};

export default Grafico;
